import type { ReactNode } from 'react'
import { Paperclip } from 'lucide-react'

export type FieldTag = 'critical' | 'strategic' | 'new'

interface BaseFieldProps {
  /** Two-level field id, e.g. "1.01". Renders in the big `.field-num` block. */
  num: string
  /** Field label — the bold line right of (or above on narrow) the number. */
  label: string
  /** Help text rendered under the label in muted type. */
  help?: string
  /** True draws a coral "required" pill next to the label. */
  required?: boolean
  /** Additional badge chips. Multiple allowed (e.g. `['critical', 'new']`). */
  tags?: FieldTag[]
  /** When true, hides the paperclip attach affordance (decorative for now). */
  noAttach?: boolean
}

interface IntakeFieldProps extends BaseFieldProps {
  /** The input control (Input / Select / Textarea / RadioOptions / DynList). */
  children: ReactNode
}

/**
 * One row of the original intake form: numbered eyebrow on the left, label
 * with optional REQUIRED / CRITICAL / STRATEGIC / NEW badges, help text,
 * the input control underneath, and a paperclip attach affordance on the
 * right. CSS lives in `styles/briefer.css` under `.field`, `.field-num`,
 * `.field-label`, `.req`, `.field-tag.tag-*`, `.field-help`.
 */
export function IntakeField({
  num,
  label,
  help,
  required,
  tags,
  noAttach,
  children,
}: IntakeFieldProps) {
  return (
    <div className="field">
      <div className="field-num">{num}</div>
      <label className="field-label">
        {label}
        {required && <span className="req"> required</span>}
        {tags?.map((tag) => (
          <span key={tag} className={`field-tag tag-${tag}`}>
            {' '}
            {tag}
          </span>
        ))}
      </label>
      {help && <p className="field-help">{help}</p>}
      {children}
      {!noAttach && (
        <button
          type="button"
          className="briefer-upload-btn"
          aria-label="Attach reference"
          title="Attach reference"
        >
          <Paperclip size={14} />
        </button>
      )}
    </div>
  )
}

/**
 * The coral-tinted blockquote that opens each section — replicates
 * `<blockquote class="pull">` from the original lorenza.html.
 */
export function SectionPremise({ quote, citation = '— Section premise' }: { quote: string; citation?: string }) {
  return (
    <blockquote className="pull">
      {quote}
      <cite>{citation}</cite>
    </blockquote>
  )
}

export interface RadioOption {
  value: string
  /** Bold short label on the option chip. */
  label: string
  /** Smaller-text description under the label. */
  desc?: string
}

interface RadioOptionsProps {
  name: string
  value?: string
  options: RadioOption[]
  onChange: (value: string) => void
}

/**
 * Multi-option radio group rendered as styled option chips. Mirrors the
 * original `.field-options[data-type="single"]` markup with `opt-label`
 * + `opt-desc`.
 */
export function RadioOptions({ name, value, options, onChange }: RadioOptionsProps) {
  return (
    <div className="field-options" role="radiogroup">
      {options.map((opt) => (
        <label key={opt.value} className={`field-option${value === opt.value ? ' field-option--selected' : ''}`}>
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />
          <div>
            <span className="opt-label">{opt.label}</span>
            {opt.desc && <span className="opt-desc">{opt.desc}</span>}
          </div>
        </label>
      ))}
    </div>
  )
}

interface CheckboxOptionsProps {
  name: string
  values: string[]
  options: RadioOption[]
  onChange: (next: string[]) => void
}

/**
 * Multi-select checkbox group rendered as styled option chips. Mirrors the
 * original `.field-options[data-type="multi"]` markup with `opt-label`
 * + `opt-desc`, so cross-impl DOM counters see the same `.field-options`
 * node the FastAPI page emits.
 */
export function CheckboxOptions({ name, values, options, onChange }: CheckboxOptionsProps) {
  function toggle(v: string) {
    const set = new Set(values)
    if (set.has(v)) set.delete(v)
    else set.add(v)
    onChange([...set])
  }
  return (
    <div className="field-options" data-type="multi">
      {options.map((opt) => {
        const checked = values.includes(opt.value)
        return (
          <label
            key={opt.value}
            className={`field-option${checked ? ' field-option--selected' : ''}`}
          >
            <input
              type="checkbox"
              name={name}
              value={opt.value}
              checked={checked}
              onChange={() => toggle(opt.value)}
            />
            <div>
              <span className="opt-label">{opt.label}</span>
              {opt.desc && <span className="opt-desc">{opt.desc}</span>}
            </div>
          </label>
        )
      })}
    </div>
  )
}
